import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    IconButton,
    Button,
    Paper,
    Stack,
    Chip,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormHelperText,
    Autocomplete,
    DialogContent,
    Dialog,
} from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "src/redux/storeHooks";
import { Spinner } from "react-bootstrap";
import Loader from "src/utils/Loader";
//import createRulesData from "src/@dummyData/createRulesData.json"
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { createRuleAsync, getActionListAsync, getAllInventoryLocationPlatformListAsync, getAllNonSecondaryPlatformListAsync, getAllSecondaryPlatformListAsync, getConditionCodeDataAsync, getConditionListAsync, getEventListAsync, getGetEditDataAsync, getSelectMultiselectByActionCodeAsync, getTriggerListAsync, updateRuleAsync } from "src/redux/actions/ruleBuilderAction";
import { CONITION_EVALUATOR, OPERATOR_MAP } from "src/utils/constants";
import { toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  buildRuleEngineListPath,
  withListStatePage,
  type RuleListState,
} from "./ruleEngineListUrl";
import PremiumBreadcrumb from "src/app/components/navigation/PremiumBreadcrumb";

// ---------- Types ----------
type Rule = {
    id: string;
    field?: string;
    conditionEvaluator?: string;
    value?: string;
    conditionDataValues?: ConditionDataSelection[];
    isGroup?: false;
    conjunctionToNext?: "AND" | "OR";
};

type Action = {
    id: string;
    type: string;
    value: string;
    actionDataValues?: ActionDataSelection[];
};

type TriggerDefinition = {
    id: string; // was trigger_id
    label: string;
    condition_code?: string;
    description?: string;
    allowed_operators: string[];
    value_input_type: "TEXT" | "SELECT" | "MULTISELECT" | "INTEGER" | "BOOLEAN" | "NONE" | "MULTILINETEXT";
    value_options?: { option_id: number; display_label: string }[];
    condition_data?: ConditionDataDefinition[];
};

type ConditionDataDefinition = {
    id: number;
    field_code: string;
    display_name: string;
    description?: string;
    field_type?: string;
    input_type: "TEXT" | "SELECT" | "MULTISELECT" | "INTEGER" | "BOOLEAN" | "NONE" | "MULTILINETEXT";
    parent?: number | null;
    children?: ConditionDataDefinition[];
};

type ConditionCodeOption = {
    option_id: number | string;
    display_label: string;
    platform_field_id?: number | null;
    platform_field_data_id?: number | string | null;
    value?: number | string;
    child_fields?: Array<ConditionDataDefinition & {
        platform_field_id?: number | null;
        options?: Array<{
            id: number | string;
            api_name?: string;
            display_name?: string;
            name?: string;
        }>;
    }>;
};

type ConditionDataSelection = {
    id?: number | string | null;
    field_code: string;
    field_type?: string | null;
    value_str: string;
    platform_field_id?: number | null;
    platform_field_data_id?: number | string | null;
    child?: ConditionDataSelection[];
};

type ActionDataSelection = {
    id?: number | string | null;
    field_code: string;
    field_type?: string | null;
    action_value: string;
    platform_field_id?: number | null;
    platform_field_data_id?: number | string | null;
    child?: ActionDataSelection[];
};

type ActionDefinition = {
    id: string; // was action_id
    parent_trigger_id: string | null;
    label: string;
    description?: string;
    value_input_type: "TEXT" | "SELECT" | "INTEGER" | "MULTISELECT" | "BOOLEAN" | "NONE" | "MULTILINETEXT";
    value_options?: { option_id: number; display_label: string }[];
    action_code?: string;
    is_multi?: boolean;
    action_data?: ConditionDataDefinition[];
};
export interface RuleItem {
    id: string;
    rule_name: string; // not ruleName
    trigger_groups: {
        group_id: string;
        logical_conjunction: "AND" | "OR";
        conditions: {
            condition_id: string;
            trigger_id: string | null;
            operator?: string;
            value?: string;
            conjunction_to_next?: "AND" | "OR";
        }[];
    }[];
    actions_to_execute: {
        action_id: string;
        value: string;
    }[];
}

interface CreateEditState {
    mode: "create" | "edit";
    ruleData?: RuleItem;
    listState?: RuleListState;
}

const ACTION_PLATFORM_API_MAP: Record<string, "SECONDARY" | "NON_SECONDARY" | "INVENTORY_LOCATION"> = {
    IGNORE_CREATE_ORDER_EVENT: "NON_SECONDARY", GENERATE_ORDER_SHIPPING_LABEL: "SECONDARY", SELECT_LOCATION_FOR_INVENTORY_SYNC: "INVENTORY_LOCATION",
};

const MULTISELECT_ACTION_CODES = new Set<string>([
    "SELECT_LOCATION_FOR_INVENTORY_SYNC",
]);

// ---------- Helpers ----------
const uid = (prefix = "") => `${prefix}${crypto.randomUUID()}`;


export const parseCsvIds = (csv?: string | number | null) =>
    String(csv ?? "")?.split(",")?.map(s => s.trim()).filter(Boolean);

export const toCsvIds = (ids: Array<string | number>) => ids.join(",");

const CONDITION_CODE_DATA_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

/** Minimal layout-only styles for condition/action rows */
const RULE_CHIP_ROW_SX = {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 2,
    p: 1,
    borderRadius: "4px",
    border: "1px solid #ccc",
    bgcolor: "#fff",
    "& .MuiInput-root": { marginTop: 0 },
    "& .MuiInput-input, & .MuiSelect-select": {
        paddingTop: "6px",
        paddingBottom: "6px",
    },
};

const RULE_CHIP_PRIMARY_SX = {
    flex: { xs: "1 1 100%", lg: "0 1 360px" },
    minWidth: { xs: "100%", sm: 220 },
    maxWidth: { lg: 400 },
};

const RULE_CHIP_OPERATOR_SX = {
    flex: "0 0 auto",
    minWidth: { xs: "100%", sm: 168 },
    maxWidth: 220,
};

const RULE_CHIP_VALUES_SX = {
    flex: "1 1 140px",
    minWidth: { xs: "100%", sm: 120 },
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: 2,
};

const RULE_CHIP_ACTION_FIELD_SX = {
    flex: "1 1 120px",
    minWidth: 120,
    maxWidth: "100%",
};

export const isBackendId = (id: any): boolean => {
    if (id === null || id === undefined) return false;
    const n = Number(id);
    return Number.isInteger(n) && n > 0;
};

const conditionOptionKey = (
    conditionCode?: string,
    fieldCode?: string,
    selectedValue?: string | number
) => `${conditionCode ?? ""}:${fieldCode ?? ""}:${selectedValue ?? ""}`;

const isConditionDataOptionInput = (inputType?: string) =>
    inputType === "SELECT" || inputType === "MULTISELECT";

const isConditionDataFreeInput = (inputType?: string) =>
    inputType === "TEXT" || inputType === "INTEGER" || inputType === "MULTILINETEXT";

export const buildConditionDataPayload = (
    definitions: ConditionDataDefinition[] = [],
    selections: ConditionDataSelection[] = [],
    conditionCode?: string
) => {
    return definitions
        .map(def => {
            const selected = selections.find(item => item.field_code === def.field_code);
            if (!selected?.value_str) return null;

            const child = buildConditionDataPayload(def.children ?? [], selected.child ?? [], conditionCode);

            return {
                ...(isBackendId(selected.id) ? { id: Number(selected.id) } : {}),
                condition_code: conditionCode ?? null,
                field_code: def.field_code,
                field_type: def.field_type ?? null,
                platform_field_id: def.field_type === "WORKFLOW" ? null : selected.platform_field_id ?? null,
                platform_field_data_id: def.field_type === "WORKFLOW" ? null : selected.platform_field_data_id ?? null,
                value_str: selected.value_str,
                ...(child.length ? { child } : {}),
            };
        })
        .filter(Boolean);
};

export const buildActionDataPayload = (
    definitions: ConditionDataDefinition[] = [],
    selections: ActionDataSelection[] = [],
    actionCode?: string
) => {
    return definitions
        .map(def => {
            const selected = selections.find(item => item.field_code === def.field_code);
            if (!selected?.action_value) return null;

            const child = buildActionDataPayload(def.children ?? [], selected.child ?? [], actionCode);

            return {
                ...(isBackendId(selected.id) ? { id: Number(selected.id) } : {}),
                action_code: actionCode ?? null,
                field_code: def.field_code,
                field_type: def.field_type ?? null,
                platform_field_id: selected.platform_field_id ?? null,
                platform_field_data_id: selected.platform_field_data_id ?? null,
                action_value: selected.action_value,
                ...(child.length ? { child } : {}),
            };
        })
        .filter(Boolean);
};

export const mapSavedActionDataToSelections = (
    savedItems: any[] = [],
    definitions: ConditionDataDefinition[] = []
): ActionDataSelection[] => {
    return definitions
        .map((def, index) => {
            const saved = savedItems.find(item => item?.field_code === def.field_code) ?? savedItems[index];
            const actionValue = saved?.action_value;
            if (actionValue === undefined || actionValue === null || actionValue === "") return null;

            return {
                ...(isBackendId(saved?.id) ? { id: saved.id } : {}),
                field_code: def.field_code,
                field_type: def.field_type ?? null,
                action_value: String(actionValue),
                platform_field_id: saved.platform_field ?? saved.platform_field_id ?? null,
                platform_field_data_id: saved.platform_field_data ?? saved.platform_field_data_id ?? null,
                child: mapSavedActionDataToSelections(saved.child ?? [], def.children ?? []),
            };
        })
        .filter(Boolean) as ActionDataSelection[];
};

export const mapSavedConditionDataToSelections = (
    savedItems: any[] = [],
    definitions: ConditionDataDefinition[] = []
): ConditionDataSelection[] => {
    return definitions
        .map((def, index) => {
            const saved = savedItems[index];
            if (!saved?.value_str) return null;

            return {
                ...(isBackendId(saved?.id) ? { id: saved.id } : {}),
                field_code: def.field_code,
                field_type: def.field_type ?? null,
                value_str: String(saved.value_str),
                platform_field_id: saved.platform_field ?? saved.platform_field_id ?? null,
                platform_field_data_id: saved.platform_field_data ?? saved.platform_field_data_id ?? null,
                child: mapSavedConditionDataToSelections(saved.child ?? [], def.children ?? []),
            };
        })
        .filter(Boolean) as ConditionDataSelection[];
};

const getConditionDataOptions = (
    conditionCodeOptionsByKey: Record<string, ConditionCodeOption[]>,
    def: ConditionDataDefinition,
    conditionCode?: string,
    parentDef?: ConditionDataDefinition,
    parentValue?: string
): ConditionCodeOption[] => {
    const key = conditionOptionKey(conditionCode, def.field_code, parentValue);

    if (!parentDef) {
        return conditionCodeOptionsByKey?.[key] ?? [];
    }

    const parentOptions = conditionCodeOptionsByKey?.[conditionOptionKey(conditionCode, parentDef.field_code)] ?? [];
    const selectedParentOption = parentValue
        ? parentOptions.find(opt => String(opt.value ?? opt.option_id) === String(parentValue))
        : undefined;
    const embeddedChildField = selectedParentOption?.child_fields?.find(child => child.field_code === def.field_code);
    const embeddedOptions: ConditionCodeOption[] = embeddedChildField?.options?.map(option => ({
        option_id: option.id,
        display_label: option.display_name || option.api_name || option.name || String(option.id),
        platform_field_id: embeddedChildField.platform_field_id ?? null,
        platform_field_data_id: option.id,
        value: option.id,
    })) ?? [];

    return embeddedOptions.length ? embeddedOptions : conditionCodeOptionsByKey?.[key] ?? [];
};

const actionOptionKey = (
    actionCode?: string,
    fieldCode?: string,
    selectedValue?: string | number
) => fieldCode
    ? `${actionCode ?? ""}:${fieldCode}:${selectedValue ?? ""}`
    : actionCode ?? "";

const getActionDataOptions = (
    actionCodeOptions: Record<string, ConditionCodeOption[]>,
    def: ConditionDataDefinition,
    actionCode?: string,
    parentDef?: ConditionDataDefinition,
    parentValue?: string
): ConditionCodeOption[] => {
    const key = actionOptionKey(actionCode, def.field_code, parentValue);
    if (!parentDef) return actionCodeOptions?.[key] ?? [];

    const parentOptions = actionCodeOptions?.[actionOptionKey(actionCode, parentDef.field_code)] ?? [];
    const selectedParentOption = parentValue
        ? parentOptions.find(opt => String(opt.value ?? opt.option_id) === String(parentValue))
        : undefined;
    const embeddedChildField = selectedParentOption?.child_fields?.find(child => child.field_code === def.field_code);
    const embeddedOptions: ConditionCodeOption[] = embeddedChildField?.options?.map(option => ({
        option_id: option.id,
        display_label: option.display_name || option.api_name || option.name || String(option.id),
        platform_field_id: embeddedChildField.platform_field_id ?? null,
        platform_field_data_id: option.id,
        value: option.id,
    })) ?? [];

    return embeddedOptions.length ? embeddedOptions : actionCodeOptions?.[key] ?? [];
};

const APPLIES_TO_TOOLTIP =
    "Choose the module in which this rule works on. Orders, Inventory or Products, as they sync for the selected channel. Your choice decides which fields appear in Conditions and which Actions are available.";

const AppliesToFieldLabel = () => (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography fontWeight={500}>Applies To</Typography>
        <Tooltip
            title={APPLIES_TO_TOOLTIP}
            placement="top"
            arrow
            enterDelay={200}
            leaveDelay={100}
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: "#212529",
                        color: "#fff",
                        fontSize: "0.95rem",
                        borderRadius: "4px",
                        padding: "6px 8px",
                        maxWidth: 360,
                        whiteSpace: "normal",
                    },
                },
                arrow: { sx: { color: "#212529" } },
            }}
        >
            <span
                aria-label="Applies To help"
                style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "darkgray",
                }}
            >
                <InfoOutlinedIcon fontSize="small" />
            </span>
        </Tooltip>
    </Stack>
);

// ---------- Main Component ----------
export default function RuleBuilder() {
    const dispatch = useAppDispatch();
    const { ruleDefinitionsLoading, saveRuleLoading, eventList, triggerList, conditionList, actionList,
        loading, editRuleData, allSecondaryList, allNonSecondaryList, allInventoryLocationList,
        selectMultiselectOptionsByActionCode, conditionCodeOptionsByKey } = useSelector((state: any) => state.ruleBuilderSlice);
    const { fetchedConnectedPlatformData } = useAppSelector(state => state.fetchConnectedPlatform);
    const filteredConnectedPlatformData = fetchedConnectedPlatformData.filter((item) => !item?.isMainStore && !item?.isAccountingStore && !item?.isShippingStore && !item?.isOmsStore);
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as CreateEditState | undefined;
    const { mode, ruleData, listState } = state || {
        mode: "create",
        ruleData: undefined,
        listState: undefined,
    };
    const listStateRef = useRef<RuleListState | undefined>(listState);

    useEffect(() => {
        if (listState) {
            listStateRef.current = listState;
        }
    }, [listState]);

    const goBackToList = (options?: { resetPage?: boolean }) => {
        const returnState = listStateRef.current;
        if (!returnState) {
            navigate("/rule-engine");
            return;
        }

        const listStateForNavigation = options?.resetPage
            ? withListStatePage(returnState, 1)
            : returnState;

        navigate(buildRuleEngineListPath(listStateForNavigation));
    };
    const [name, setName] = useState("");
    const [conditions, setConditions] = useState<Rule[]>([]);
    const [actions, setActions] = useState<Action[]>([]);
    const [triggers, setTriggers] = useState<TriggerDefinition[]>([]);
    const [actionDefs, setActionDefs] = useState<ActionDefinition[]>([]);
    const [selectedPlatformId, setSelectedPlatformId] = useState<number | "">("");
    const [selectedEventId, setSelectedEventId] = useState<number | "">("");
    const [selectedTriggerId, setSelectedTriggerId] = useState<number | "">("");
    const [errors, setErrors] = useState<{
        name?: boolean;
        platform?: boolean;
        event?: boolean;
        groups: Record<string, Record<string, { field?: boolean; operator?: boolean; value?: boolean }>>;
        actions: Record<string, { type?: boolean; value?: boolean }>;
    }>({ groups: {}, actions: {} });
    const ruleId = ruleData?.id;
    const [status, setStatus] = useState(true);

    // Local state for fetched rule
    const [fetchedRule, setFetchedRule] = useState<RuleItem | null>(null);
    const [editLoadFailed, setEditLoadFailed] = useState(false);
    const isPrefilling = mode === "edit" && loading.editRule;

    useEffect(() => {
        if (mode === "edit" && ruleId) {
            setEditLoadFailed(false);
            dispatch(getGetEditDataAsync({ ruleId: Number(ruleId) }))
                .unwrap()
                .then((res: any) => {
                    setFetchedRule(res.data); // Save in local state
                    setEditLoadFailed(false);
                })
                .catch(err => {
                    setEditLoadFailed(true);
                    toast.error("Failed to fetch rule data");
                });
        }
    }, [mode, ruleId, dispatch]);

    useEffect(() => {
        if (selectedPlatformId) {
            dispatch(getEventListAsync({ platformId: selectedPlatformId }));
        }
    }, [selectedPlatformId]);

    useEffect(() => {
        if (mode === "create") {
            setConditions([
                {
                    id: uid("ui_cond_"),
                    field: "",
                    conditionEvaluator: "",
                    value: "",
                    conjunctionToNext: "AND",
                },
            ]);

            setActions([
                {
                    id: uid("ui_act_"),
                    type: "",
                    value: "",
                },
            ]);
        }
    }, [mode]);

    useEffect(() => {
        if (!selectedEventId) {
            setTriggers([]);
            setActionDefs([]);
            return;
        }
        dispatch(getConditionListAsync({ triggerId: selectedEventId, platformId: selectedPlatformId }));
        dispatch(getActionListAsync({ triggerId: selectedEventId, platformId: selectedPlatformId }));
        // dispatch(getAllSecondaryPlatformListAsync());
        // dispatch(getAllNonSecondaryPlatformListAsync());
    }, [selectedEventId, mode]);

    useEffect(() => {
        if (!Array.isArray(conditionList) || conditionList.length === 0) {
            setTriggers([]);
            return;
        }
        const normalized = conditionList.map((c: any) => ({
            id: c.id,
            label: c.display_name,
            condition_code: c.condition_code ?? "",
            description: c.description,
            allowed_operators: c.allowed_operators ?? CONITION_EVALUATOR.map(e => e.key),
            value_input_type: c.input_type ?? "TEXT",
            value_options: c.value_options ?? [],
            condition_data: c.condition_data ?? [],
        }));

        setTriggers(normalized);
    }, [conditionList]);

    useEffect(() => {
        if (!Array.isArray(actionList) || actionList.length === 0) {
            setActionDefs([]);
            return;
        }
        const normalized = actionList.map((a: any) => ({
            id: a.id,
            label: a.display_name,
            description: a.description,
            parent_trigger_id: a.available_workflow_event_trigger_id ?? null,
            value_input_type: a.input_type ?? "TEXT",
            value_options: a.value_options ?? [],
            action_code: a?.action_code ?? "",
            action_data: a.action_data ?? [],
        }));

        setActionDefs(normalized);
    }, [actionList]);

    // ---------- Populate edit mode data ----------
    useEffect(() => {
        if (mode === "edit" && editRuleData) {
            const data = editRuleData;

            const initialConditions =
                (data.workflow_conditions && data.workflow_conditions.length > 0
                    ? data.workflow_conditions.map((c: any) => {
                        const conditionDefinitions = c?.available_workflow_event_condition?.condition_data ?? [];

                        return {
                            condition_id: c?.id || uid(),
                            trigger_id: c?.available_workflow_event_condition?.display_name || "",
                            operator: c.operation || "",
                            value: c.value_str || "",
                            conditionDataValues: mapSavedConditionDataToSelections(c.condition_data || [], conditionDefinitions),
                            conjunction_to_next: c.condition_evaluator || "AND",
                        };
                    })
                    : [
                        {
                            condition_id: uid("c_"),
                            trigger_id: data.available_workflow_event_trigger_id?.toString() || "",
                            operator: "",
                            value: "",
                            conjunction_to_next: "AND",
                        },
                    ]);

            const initialActions =
                (data?.workflow_actions && data?.workflow_actions.length > 0
                    ? data?.workflow_actions?.map((a: any) => ({
                        action_id: a?.id || "",
                        type: a?.available_workflow_event_action?.display_name || "",
                        value: String(a.action_value ?? ""),
                        action_code: a?.available_workflow_event_action?.action_code,
                        actionDataValues: mapSavedActionDataToSelections(
                            a.action_data || [],
                            a?.available_workflow_event_action?.action_data || []
                        ),
                    }))
                    : [
                        {
                            action_id: uid("a_"),
                            type: "",
                            value: "",
                            actionDataValues: [],
                        },
                    ]);

            const mappedRule: RuleItem = {
                id: data.id.toString(),
                rule_name: data.name,
                trigger_groups: [
                    {
                        group_id: "group_1",
                        logical_conjunction: "AND",
                        conditions: initialConditions,
                    },
                ],
                actions_to_execute: initialActions,
            };

            setFetchedRule(mappedRule);

            // set other states
            setName(mappedRule.rule_name);
            setSelectedPlatformId(data.platform_id || "");
            setSelectedEventId(data.available_workflow_event_id || "");
            setSelectedTriggerId(data.available_workflow_event_trigger_id || "");
            setStatus(Boolean(editRuleData.is_active));

            // update local UI states
            setConditions(initialConditions.map(c => ({
                id: c?.condition_id || "",
                field: c?.trigger_id || "",
                conditionEvaluator: c?.operator || "",
                value: c.value || "",
                conditionDataValues: c.conditionDataValues || [],
                conjunctionToNext: c?.conjunction_to_next || "AND",
            })));

            setActions(initialActions.map(a => ({
                id: a?.action_id,
                type: a?.type,
                value: a.value,
                actionDataValues: a.actionDataValues || [],
            })));
        }
    }, [mode, editRuleData]);

    useEffect(() => {
        if (!actionDefs.length) return;

        setActionDefs(prev =>
            prev.map(action => {
                // IGNORE_CREATE_ORDER_EVENT → Non-secondary
                if (
                    action.action_code === "IGNORE_CREATE_ORDER_EVENT" &&
                    allNonSecondaryList?.length
                ) {
                    return {
                        ...action,
                        value_input_type: "SELECT",
                        value_options: allNonSecondaryList.map((item: any) => ({
                            option_id: item.id,
                            display_label: item.display_name,
                        })),
                    };
                }

                // GENERATE_ORDER_SHIPPING_LABEL → Secondary
                if (
                    action.action_code === "GENERATE_ORDER_SHIPPING_LABEL" &&
                    allSecondaryList?.length
                ) {
                    return {
                        ...action,
                        value_input_type: "SELECT",
                        value_options: allSecondaryList.map((item: any) => ({
                            option_id: item.id,
                            display_label: item.display_name,
                        })),
                    };
                }
                if (action.action_code === "SELECT_LOCATION_FOR_INVENTORY_SYNC" && allInventoryLocationList?.length) {
                    return {
                        ...action,
                        value_input_type: "MULTISELECT",
                        value_options: allInventoryLocationList.map((item: any) => ({
                            option_id: item.id,
                            display_label: item.api_name,
                        })),
                    };
                }
                return action;
            })
        );
    }, [allSecondaryList, allNonSecondaryList, allInventoryLocationList]);

    // ---------- Validation ----------
    const validateAll = (): boolean => {
        let valid = true;
        const newGroupErrors: typeof errors.groups = {};
        const newActionErrors: typeof errors.actions = {};
        // ----------------- Rule Name ------------------
        if (!name.trim()) {
            valid = false;
        }
        // ------------------ Platform / Event ------------------
        const platformError = !selectedPlatformId;
        const eventError = !!selectedPlatformId && !selectedEventId;

        if (platformError || eventError) {
            valid = false;
        }
        // ------------------ Conditions ------------------
        if (!conditions.length) {
            valid = false;
        }

        conditions.forEach(rule => {
            newGroupErrors["conditions"] ??= {};
            const triggerDef = triggers.find(t => t.label === rule.field);
            const hasConditionData = Boolean(triggerDef?.condition_data?.length);
            const requiresValue = triggerDef && triggerDef.value_input_type !== "NONE" && !hasConditionData;
            const requiresConditionData = Boolean(
                triggerDef?.condition_data?.some(def => {
                    const selected = rule.conditionDataValues?.find(item => item.field_code === def.field_code);
                    if (!selected?.value_str) return true;
                    return Boolean(
                        def.children?.some(childDef => {
                            if (childDef.input_type === "NONE") return false;
                            const child = selected.child?.find(item => item.field_code === childDef.field_code);
                            if (isConditionDataFreeInput(childDef.input_type)) {
                                return !child?.value_str?.trim();
                            }
                            if (!isConditionDataOptionInput(childDef.input_type)) {
                                return !child?.value_str;
                            }
                            const childOptions = getConditionDataOptions(
                                conditionCodeOptionsByKey,
                                childDef,
                                triggerDef?.condition_code,
                                def,
                                selected.value_str
                            );
                            if (!childOptions.length) return false;

                            return !child?.value_str;
                        })
                    );
                })
            );
            const fieldError = !rule.field;
            const operatorError = requiresValue && !rule.conditionEvaluator;
            const valueError = (requiresValue && !rule.value) || requiresConditionData;
            newGroupErrors["conditions"][rule.id] = {
                field: fieldError,
                operator: operatorError,
                value: valueError,
            };
            if (fieldError || operatorError || valueError) {
                valid = false;
            }
        });

        // ------------------ Actions ------------------
        if (!actions.length) {
            valid = false;
        }
        actions.forEach(action => {
            const actionDef = actionDefs.find(a => a.label === action.type);
            const actionDataDefinitions = actionDef?.action_data ?? [];
            const hasActionData = actionDataDefinitions.length > 0;
            const requiresValue = actionDef && actionDef.value_input_type !== "NONE" && !hasActionData;
            const requiresActionData = actionDataDefinitions.some(def => {
                if (def.input_type === "NONE") return false;
                const selected = action.actionDataValues?.find(item => item.field_code === def.field_code);
                if (!selected?.action_value) return true;
                return Boolean(def.children?.some(childDef => {
                    if (childDef.input_type === "NONE") return false;
                    return !selected.child?.find(item => item.field_code === childDef.field_code)?.action_value;
                }));
            });
            const typeError = !action.type;
            const valueError = (requiresValue && !action.value) || requiresActionData;
            newActionErrors[action.id] = {
                type: typeError,
                value: valueError,
            };
            if (typeError || valueError) {
                valid = false;
            }
        });
        // ------------------ Set Errors ------------------
        setErrors({
            name: !name.trim(),
            platform: platformError,
            event: eventError,
            groups: newGroupErrors,
            actions: newActionErrors,
        });

        return valid;
    };

    // ---------- Live error clearing ----------
    const clearRuleError = (groupId: string, ruleId: string, field: keyof Rule) => {
        setErrors(prev => ({
            ...prev,
            groups: {
                ...prev.groups,
                [groupId]: {
                    ...prev.groups[groupId],
                    [ruleId]: {
                        ...prev.groups[groupId]?.[ruleId],
                        [field]: false,
                    },
                },
            },
        }));
    };

    const clearActionError = (actionId: string, field: keyof Action) => {
        setErrors(prev => ({
            ...prev,
            actions: {
                ...prev.actions,
                [actionId]: {
                    ...prev.actions[actionId],
                    [field]: false,
                },
            },
        }));
    };

    const clearNameError = () => setErrors(prev => ({ ...prev, name: false }));

    // ---------- Rule Logic ----------

    const toggleConjunction = (id: string) => {
        setConditions(prev => {
            const clicked = prev.find(c => c.id === id);
            if (!clicked) return prev;
            const newConjunction = clicked.conjunctionToNext === "AND" ? "OR" : "AND";
            // Apply to all conditions
            return prev.map(c => ({ ...c, conjunctionToNext: newConjunction }));
        });
    };
    const addCondition = () => {
        setConditions(prev => [
            ...prev,
            {
                id: uid("ui_cond_"),
                field: "",
                conditionEvaluator: "",
                value: "",
                conjunctionToNext: "AND",
            },
        ]);
    };

    const updateCondition = (id: string, patch: Partial<Rule>) => {
        setConditions(prev =>
            prev.map(c => (c.id === id ? { ...c, ...patch } : c))
        );
    };
    const deleteCondition = (id: string) => {
        setConditions(prev => prev.filter(c => c.id !== id));
    };

    // ---------- Action Logic ----------
    const addAction = () => {
        setActions(prev => [
            ...prev, { id: uid("a_"), type: "", value: "" }
        ]);
    };

    const updateAction = (id: string, patch: Partial<Action>) => {
        setActions(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)));
    };

    const deleteAction = (id: string) => {
        setActions(prev => prev.filter(a => a.id !== id));
    };

    const buildApiPayload = () => {
        const workflow_conditions = conditions.map((c, index) => {
            // const existingCondition = fetchedRule?.trigger_groups[0]?.conditions[index];
            const conditionDef = triggers.find(t => t.label === c.field);
            const conditionData = buildConditionDataPayload(
                conditionDef?.condition_data,
                c.conditionDataValues,
                conditionDef?.condition_code
            );
            return {
                ...(isBackendId(c.id) ? { id: Number(c.id) } : {}),
                available_workflow_event_condition_id: conditionDef?.id || c.id,
                condition_code: conditionDef?.condition_code || "",
                operation: conditionData.length ? "EQUALS" : c.conditionEvaluator || "",
                condition_evaluator: c.conjunctionToNext,
                platform_field_id: null,
                platform_field_data_id: null,
                value_str: conditionData.length ? null : c.value,
                ...(conditionData.length ? { condition_data: conditionData } : {}),
            };
        });

        const workflow_actions = actions.map((a, index) => {
            // const existingAction = fetchedRule?.actions_to_execute[index];
            const actionDef = actionDefs.find(ad => ad.label === a.type);
            const actionData = buildActionDataPayload(
                actionDef?.action_data,
                a.actionDataValues,
                actionDef?.action_code
            );
            return {
                ...(isBackendId(a.id) ? { id: Number(a.id) } : {}),
                available_workflow_event_action_id: actionDef?.id || a.id,
                action_value: actionData.length ? null : a.value,
                action_data: actionData,
            };
        });

        if (mode === "edit" && ruleData) {
            return {
                platform_workflow_id: ruleData.id,
                name,
                platform_id: selectedPlatformId,
                available_workflow_event_id: selectedEventId,
                workflow_conditions,
                workflow_actions,
                is_active: status,
            };
        }

        return {
            name,
            platform_id: selectedPlatformId,
            available_workflow_event_id: selectedEventId,
            workflow_conditions,
            workflow_actions,
        };
    };


    // ---------- API Functions ----------
    const saveRule = async () => {
        const isValid = validateAll();
        if (!isValid) {
            toast.error("Please fix the required fields before saving the rule.");
            return;
        }

        const payload = buildApiPayload();
        console.log(payload, "payload to be sent to API");
        try {
            if (mode === "create") {
                await dispatch(createRuleAsync(payload)).unwrap();
                toast.success("Rule created successfully!");
            } else {
                await dispatch(updateRuleAsync(payload)).unwrap();
                toast.success("Rule updated successfully!");
            }
            goBackToList({ resetPage: mode === "create" });
        } catch (err: any) {
            console.log("err-hi", err, err?.data);
            toast.error(err?.data?.massage || "Something went wrong!");
        }
    };

    // ---------- Render ----------
    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f1f5f9", p: "15px" }}>
            <Dialog
                open={isPrefilling}
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        px: 3,
                        py: 2,
                    }}
                >
                    <Loader variant="section" />
                    {/* <Typography fontWeight={600}>Prefilling rule...</Typography> */}
                </DialogContent>
            </Dialog>
            <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                <PremiumBreadcrumb
                    items={[
                        {
                            label: "Automation Rules",
                            to: listState ? buildRuleEngineListPath(listState) : "/rule-engine",
                        },
                        { label: mode === "create" ? "Create Rule" : "Edit Rule" },
                    ]}
                />
            </Box>
            <Paper
                elevation={3}
                sx={{ maxWidth: "1200px", mx: "auto", borderRadius: 2, overflow: "hidden", bgcolor: "#fff", }}
            >
                {/* ---------- Header ---------- */}
                <Box sx={{
                    px: 3, py: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                >
                    <Typography fontWeight={600} fontSize="1.25rem" fontFamily="Roboto, Helvetica, Arial, sans-serif">
                        {mode === "create" ? "Create Rule" : "Edit Rule"}
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        {editLoadFailed && (
                            <button
                                type="button"
                                className="btn btn-light-danger btn-sm"
                                onClick={() => {
                                    if (ruleId) {
                                        setEditLoadFailed(false);
                                        dispatch(getGetEditDataAsync({ ruleId: Number(ruleId) }));
                                    }
                                }}
                            >
                                Retry Load
                            </button>
                        )}
                        {/* Cancel Button */}
                        <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={() => goBackToList()}
                        >
                            Cancel
                        </button>

                        {/* Save / Update Button */}
                        {saveRule && (
                            <button
                                type="button"
                                className={`btn btn-success btn-sm ${saveRuleLoading || isPrefilling ? "disabled" : ""}`}
                                onClick={saveRule}
                                disabled={saveRuleLoading || isPrefilling}
                            >
                                {saveRuleLoading
                                    ? "Saving..."
                                    : mode === "create"
                                        ? "Save Rule"
                                        : "Update Rule"}
                            </button>
                        )}
                    </Stack>

                </Box>

                {/* ---------- Content ---------- */}
                <Box sx={{ p: 3 }}>
                    {/* ---------- Row 1: Rule Name + Status ---------- */}
                    <Box sx={{ mb: 3, display: "grid", gridTemplateColumns: mode === "create" ? "1fr 1fr 1fr" : "1fr 260px", gap: 3, alignItems: "start", }}
                    >
                        {/* ---------- Rule Name ---------- */}
                        <Box>
                            <Typography fontWeight={500} mb={0.5}> Rule Name </Typography>
                            <TextField
                                fullWidth
                                placeholder="Set Rule Name"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    clearNameError();
                                }}
                                error={errors.name}
                                helperText={errors.name ? "Required" : ""}
                                sx={{
                                    "& .MuiOutlinedInput-input": { height: "3px", },
                                }}
                            />
                        </Box>

                        {/* ---------- Channel (CREATE ONLY) ---------- */}
                        {mode === "create" && (
                            <Box>
                                <Typography fontWeight={500} mb={0.5}> Channel </Typography>
                                <FormControl fullWidth size="small" error={errors.platform}>
                                    <Select
                                        value={selectedPlatformId}
                                        onChange={(e) => {
                                            setSelectedPlatformId(Number(e.target.value));
                                            setSelectedEventId("");
                                            setErrors(prev => ({ ...prev, platform: false, event: false }));
                                        }}
                                    >
                                        {filteredConnectedPlatformData.map((p: any) => (
                                            <MenuItem key={p.id} value={p.id}>
                                                {p.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.platform && <FormHelperText>Required</FormHelperText>}
                                </FormControl>
                            </Box>
                        )}

                        {/* ---------- Event (CREATE ONLY) ---------- */}
                        {mode === "create" && (
                            <Box>
                                <AppliesToFieldLabel />
                                <FormControl fullWidth size="small" error={errors.event}>
                                    <Select
                                        value={selectedEventId}
                                        disabled={!selectedPlatformId}
                                        onChange={(e) => {
                                            setSelectedEventId(Number(e.target.value));
                                            setErrors(prev => ({ ...prev, event: false }));
                                        }}
                                    >
                                        {eventList?.map((ev: any) => (
                                            <MenuItem key={ev.id} value={ev.id}>
                                                {ev.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.event && <FormHelperText>Required</FormHelperText>}
                                </FormControl>
                            </Box>
                        )}
                        {/* ---------- Status (Edit mode only) ---------- */}
                        {mode === "edit" && (
                            <Box>
                                <Typography fontWeight={500} mb={0.5}>
                                    Status
                                </Typography>

                                <RadioGroup
                                    row
                                    value={status ? "active" : "inactive"}
                                    onChange={(e) => setStatus(e.target.value === "active")}
                                    sx={{ mt: "2px" }} // small visual alignment tweak
                                >
                                    <FormControlLabel
                                        value="active"
                                        control={<Radio size="small" />}
                                        label="Active"
                                    />
                                    <FormControlLabel
                                        value="inactive"
                                        control={<Radio size="small" />}
                                        label="Inactive"
                                    />
                                </RadioGroup>
                            </Box>
                        )}
                    </Box>

                    {/* ---------- Row 2: Channel | Event ---------- */}
                    {mode === "edit" && (
                        <Box
                            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 4, }}
                        >
                            {/* ---------- Channel ---------- */}
                            <Box>
                                <Typography fontWeight={500} mb={0.5}>
                                    Channel
                                </Typography>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    error={errors.platform}
                                >
                                    <Select
                                        fullWidth
                                        size="small"
                                        disabled
                                        value={selectedPlatformId}
                                        onChange={(e) => {
                                            setSelectedPlatformId(Number(e.target.value));
                                            setSelectedEventId("");
                                            setErrors(prev => ({
                                                ...prev,
                                                platform: false,
                                                event: false,
                                            }));
                                        }}

                                    >
                                        {filteredConnectedPlatformData?.map((p: any) => (
                                            <MenuItem key={p.id} value={p.id}>
                                                {p.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.platform && (
                                        <FormHelperText>Required</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>

                            {/* ---------- Applies To ---------- */}
                            <Box>
                                <AppliesToFieldLabel />
                                <FormControl
                                    fullWidth
                                    size="small"
                                    error={errors.event}
                                    disabled={mode === "edit" || !selectedPlatformId}
                                >
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={selectedEventId}
                                        disabled={mode === "edit" || !selectedPlatformId}
                                        onChange={(e) => {
                                            setSelectedEventId(Number(e.target.value));
                                            setErrors(prev => ({
                                                ...prev,
                                                event: false,
                                            }));
                                        }}
                                    >
                                        {eventList?.map((ev: any) => (
                                            <MenuItem key={ev.id} value={ev.id}>
                                                {ev.display_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.event && (
                                        <FormHelperText>Required</FormHelperText>
                                    )}
                                </FormControl>
                            </Box>
                        </Box>
                    )}

                    {/* ---------- Conditions ---------- */}
                    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, mb: 1 }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", gap: "10rem", flexWrap: "wrap", mb: 1, }}
                        >
                            <Typography fontWeight={600} whiteSpace="nowrap">
                                Conditions
                            </Typography>
                            <Typography fontSize="0.85rem" color="text.secondary">
                                Define one or more conditions that must be met to execute the Action
                            </Typography>
                        </Box>

                        <Box sx={{ p: 2, position: "relative" }}>
                            {/* Vertical connector */}
                            <Box sx={{ position: "absolute", left: "22px", top: "65px", bottom: "45px", width: "2px", bgcolor: "#d1d5db", }}
                            />
                            <Stack spacing={4} pl={4}>
                                {conditions.map((rule, index) => (
                                    <Box key={rule.id} sx={{ position: "relative", }}>
                                        {/* Dot */}
                                        <Box sx={{
                                            position: "absolute", left: "-30px", top: "50%", transform: "translateY(-50%)",
                                            width: 10, height: 10, borderRadius: "50%", bgcolor: "#2563eb", border: "2px solid #fff", boxShadow: "0 0 0 2px #2563eb",
                                        }}
                                        />
                                        <Typography fontWeight={500} mb={0.5}>
                                            Condition {index + 1}
                                        </Typography>

                                        <RuleChip
                                            groupId="conditions"
                                            rule={rule}
                                            triggers={triggers}
                                            onChange={patch => updateCondition(rule.id, patch)}
                                            onDelete={
                                                conditions.length > 1
                                                    ? () => deleteCondition(rule.id)
                                                    : undefined
                                            } errorState={errors.groups.conditions?.[rule.id]}
                                            clearRuleError={clearRuleError}
                                            selectedEventId={selectedEventId}
                                            selectedPlatformId={selectedPlatformId}
                                            conditionCodeOptionsByKey={conditionCodeOptionsByKey}
                                        />
                                        {index < conditions.length - 1 && (
                                            <Stack alignItems="center" mt={2}>
                                                <Box
                                                    onClick={() => toggleConjunction(rule.id)}
                                                    sx={{
                                                        position: "relative", top: "1.7rem", cursor: "pointer", px: 2,
                                                        py: 0.5, borderRadius: 20, bgcolor: "#2563eb", color: "#fff", fontWeight: 700, fontSize: "0.75rem", display: "flex", alignItems: "center", gap: 0.5, boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                                        "&:hover": {
                                                            bgcolor: "#1e40af", transform: "scale(1.05)"
                                                        },
                                                    }}
                                                >
                                                    {rule.conjunctionToNext}
                                                    <span style={{ fontSize: "0.7rem" }}>⇅</span>
                                                </Box>
                                            </Stack>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Box>

                    <button type='button'
                        className='btn btn-primary btn-sm align-self-center' onClick={addCondition}>
                        + Add Condition
                    </button>

                    {/* ---------- Actions ---------- */}
                    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, mt: 3 }}>
                        <Box
                            sx={{ px: 2, py: 1.5, bgcolor: "#f1f5f9", display: "flex", alignItems: "center", gap: "10rem", flexWrap: "wrap" }}
                        >
                            <Typography fontWeight={600} whiteSpace="nowrap">
                                Actions
                            </Typography>
                            <Typography fontSize="0.85rem" color="text.secondary" style={{ marginLeft: "20px" }}>
                                Define one or more actions to be performed when ALL conditions are met.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                            <Stack spacing={2} style={{ paddingLeft: "25px" }}>
                                {actions.map((action, index) => (
                                    <Box key={action.id}>
                                        <Typography fontWeight={500} mb={0.5}>
                                            Action {index + 1}
                                        </Typography>
                                        <ActionChip
                                            action={action}
                                            actionDefs={actionDefs}
                                            onChange={patch => updateAction(action.id, patch)}
                                            onDelete={
                                                actions.length > 1
                                                    ? () => deleteAction(action.id)
                                                    : undefined
                                            }
                                            errorState={errors.actions[action.id]}
                                            clearActionError={clearActionError}
                                            selectedEventId={selectedEventId}
                                            selectedPlatformId={selectedPlatformId}
                                            allInventoryLocationList={allInventoryLocationList}
                                            actionCodeOptions={selectMultiselectOptionsByActionCode}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm align-self-center mt-3"
                        onClick={addAction}
                    >
                        + Add Action
                    </button>
                </Box>
            </Paper>
        </Box>
    );
}

// ---------- RuleChip ----------
function RuleChip({
    groupId,
    rule,
    triggers,
    onChange,
    onDelete,
    errorState,
    clearRuleError,
    selectedEventId,
    selectedPlatformId,
    conditionCodeOptionsByKey,
}: {
    groupId: string;
    rule: Rule;
    triggers: TriggerDefinition[];
    onChange: (patch: Partial<Rule>) => void;
    onDelete?: () => void;
    errorState?: { field?: boolean; operator?: boolean; value?: boolean };
    clearRuleError: (groupId: string, ruleId: string, field: keyof Rule) => void;
    selectedEventId: number | "";
    selectedPlatformId: number | "";
    conditionCodeOptionsByKey: Record<string, ConditionCodeOption[]>;
}) {
    const dispatch = useAppDispatch();
    const { conditionCodeHasMore } = useSelector((state: any) => state.ruleBuilderSlice);
    const selectedTrigger = React.useMemo(() => triggers.find(t => t.label === rule.field), [triggers, rule.field]);
    const conditionCode = selectedTrigger?.condition_code ?? "";
    const searchTimeoutRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const pageRef = React.useRef<Record<string, number>>({});
    const isFetchingRef = React.useRef<Record<string, boolean>>({});
    const abortRef = React.useRef<Record<string, { abort: () => void } & Promise<any>>>({});
    // Tracks the last typed search per key so onOpen can restore full list if user blurred mid-search
    const activeSearchQueryRef = React.useRef<Record<string, string>>({});

    React.useEffect(() => () => {
        Object.values(searchTimeoutRef.current).forEach(t => clearTimeout(t));
    }, []);

    const operatorList = React.useMemo(() => {
        return OPERATOR_MAP[selectedTrigger?.value_input_type ?? "TEXT"] ?? [];
    }, [selectedTrigger?.value_input_type]);
    const conditionDataDefinitions = selectedTrigger?.condition_data ?? [];

    const fetchConditionDataOptions = (
        def: ConditionDataDefinition,
        parentSelectedValue?: string,
        search?: string,
        page = 1
    ) => {
        if (
            !conditionCode ||
            !def.field_code ||
            (def.input_type !== "SELECT" && def.input_type !== "MULTISELECT")
        ) {
            return;
        }

        const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
        pageRef.current[key] = page;

        // Abort any previous in-flight request for this key (prevents stale overwrites)
        abortRef.current[key]?.abort();

        const thunkResult = dispatch(getConditionCodeDataAsync({
            conditionCode,
            fieldCode: def.field_code,
            limit: CONDITION_CODE_DATA_LIMIT,
            page,
            platformId: selectedPlatformId,
            ...(parentSelectedValue ? { selectedValue: parentSelectedValue } : {}),
            ...(search?.trim() ? { search: search.trim() } : {}),
        })) as any;
        abortRef.current[key] = thunkResult;

        // Only release the scroll lock if this is still the current request
        thunkResult.finally(() => {
            if (abortRef.current[key] === thunkResult) {
                isFetchingRef.current[key] = false;
            }
        });
    };

    const debouncedFetchConditionDataOptions = (
        def: ConditionDataDefinition,
        parentSelectedValue?: string,
        search?: string
    ) => {
        const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
        if (searchTimeoutRef.current[key]) clearTimeout(searchTimeoutRef.current[key]);
        searchTimeoutRef.current[key] = setTimeout(() => {
            pageRef.current[key] = 1;
            fetchConditionDataOptions(def, parentSelectedValue, search, 1);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleConditionListboxScroll = (
        event: React.UIEvent<HTMLUListElement>,
        def: ConditionDataDefinition,
        parentSelectedValue?: string
    ) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
        const hasMore = Boolean(conditionCodeHasMore?.[key]);

        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isFetchingRef.current[key]) {
            isFetchingRef.current[key] = true;
            const currentPage = pageRef.current[key] ?? 1;
            const nextPage = currentPage + 1;
            fetchConditionDataOptions(def, parentSelectedValue, undefined, nextPage);
        }
    };

    useEffect(() => {
        if (!conditionCode) return;
        conditionDataDefinitions.forEach(def => {
            const key = conditionOptionKey(conditionCode, def.field_code);
            if (!conditionCodeOptionsByKey?.[key]?.length) {
                fetchConditionDataOptions(def);
            }
            const selected = rule.conditionDataValues?.find(item => item.field_code === def.field_code);
            if (selected?.value_str && def.children?.length) {
                def.children.forEach(childDef => {
                    const childKey = conditionOptionKey(conditionCode, childDef.field_code, selected.value_str);
                    if (!conditionCodeOptionsByKey?.[childKey]?.length) {
                        fetchConditionDataOptions(childDef, selected.value_str);
                    }
                });
            }
        });
    }, [conditionDataDefinitions, conditionCode, rule.conditionDataValues, dispatch]);

    const updateConditionDataValue = (
        def: ConditionDataDefinition,
        option: ConditionCodeOption,
        parentDef?: ConditionDataDefinition
    ) => {
        const value = String(option.value ?? option.option_id ?? "");
        const existing = !parentDef
            ? (rule.conditionDataValues ?? []).find(item => item.field_code === def.field_code)
            : (rule.conditionDataValues ?? [])
                .find(item => item.field_code === parentDef.field_code)
                ?.child?.find(item => item.field_code === def.field_code);

        const selection: ConditionDataSelection = {
            ...(existing?.id ? { id: existing.id } : {}),
            field_code: def.field_code,
            field_type: def.field_type ?? null,
            value_str: value,
            platform_field_id: def.field_type === "WORKFLOW" ? null : option.platform_field_id ?? null,
            platform_field_data_id: def.field_type === "WORKFLOW" ? null : option.platform_field_data_id ?? option.option_id ?? null,
        };

        if (!parentDef) {
            const next = [
                ...(rule.conditionDataValues ?? []).filter(item => item.field_code !== def.field_code),
                selection,
            ];
            onChange({ conditionDataValues: next, value: "" });
            clearRuleError(groupId, rule.id, "value");
            return;
        }

        const next = (rule.conditionDataValues ?? []).map(parent => {
            if (parent.field_code !== parentDef.field_code) return parent;
            return {
                ...parent,
                child: [
                    ...(parent.child ?? []).filter(item => item.field_code !== def.field_code),
                    selection,
                ],
            };
        });

        onChange({ conditionDataValues: next, value: "" });
        clearRuleError(groupId, rule.id, "value");
    };

    const updateConditionDataMultiValue = (
        def: ConditionDataDefinition,
        selectedOptions: ConditionCodeOption[],
        parentDef?: ConditionDataDefinition
    ) => {
        if (!selectedOptions.length) {
            if (!parentDef) {
                onChange({
                    conditionDataValues: (rule.conditionDataValues ?? []).filter(item => item.field_code !== def.field_code),
                    value: "",
                });
            } else {
                onChange({
                    conditionDataValues: (rule.conditionDataValues ?? []).map(parent => {
                        if (parent.field_code !== parentDef.field_code) return parent;
                        return {
                            ...parent,
                            child: (parent.child ?? []).filter(item => item.field_code !== def.field_code),
                        };
                    }),
                    value: "",
                });
            }
            clearRuleError(groupId, rule.id, "value");
            return;
        }

        const ids = selectedOptions.map(opt => String(opt.value ?? opt.option_id ?? ""));
        const dataIds = selectedOptions
            .map(opt => opt.platform_field_data_id ?? opt.option_id ?? "")
            .filter(value => value !== "" && value !== null && value !== undefined)
            .map(String);
        const existing = !parentDef
            ? (rule.conditionDataValues ?? []).find(item => item.field_code === def.field_code)
            : (rule.conditionDataValues ?? [])
                .find(item => item.field_code === parentDef.field_code)
                ?.child?.find(item => item.field_code === def.field_code);

        const selection: ConditionDataSelection = {
            ...(existing?.id ? { id: existing.id } : {}),
            field_code: def.field_code,
            field_type: def.field_type ?? null,
            value_str: toCsvIds(ids),
            platform_field_id: def.field_type === "WORKFLOW" ? null : selectedOptions[0]?.platform_field_id ?? null,
            platform_field_data_id: def.field_type === "WORKFLOW"
                ? null
                : (dataIds.length ? toCsvIds(dataIds) : null),
        };

        if (!parentDef) {
            onChange({
                conditionDataValues: [
                    ...(rule.conditionDataValues ?? []).filter(item => item.field_code !== def.field_code),
                    selection,
                ],
                value: "",
            });
            clearRuleError(groupId, rule.id, "value");
            return;
        }

        onChange({
            conditionDataValues: (rule.conditionDataValues ?? []).map(parent => {
                if (parent.field_code !== parentDef.field_code) return parent;
                return {
                    ...parent,
                    child: [
                        ...(parent.child ?? []).filter(item => item.field_code !== def.field_code),
                        selection,
                    ],
                };
            }),
            value: "",
        });
        clearRuleError(groupId, rule.id, "value");
    };

    const updateConditionDataTextValue = (
        def: ConditionDataDefinition,
        value: string,
        parentDef?: ConditionDataDefinition
    ) => {
        const existing = !parentDef
            ? (rule.conditionDataValues ?? []).find(item => item.field_code === def.field_code)
            : (rule.conditionDataValues ?? [])
                .find(item => item.field_code === parentDef.field_code)
                ?.child?.find(item => item.field_code === def.field_code);

        const selection: ConditionDataSelection = {
            ...(existing?.id ? { id: existing.id } : {}),
            field_code: def.field_code,
            field_type: def.field_type ?? null,
            value_str: value,
            platform_field_id: null,
            platform_field_data_id: null,
        };

        if (!parentDef) {
            const next = value.trim()
                ? [
                    ...(rule.conditionDataValues ?? []).filter(item => item.field_code !== def.field_code),
                    selection,
                ]
                : (rule.conditionDataValues ?? []).filter(item => item.field_code !== def.field_code);
            onChange({ conditionDataValues: next, value: "" });
        } else {
            const next = (rule.conditionDataValues ?? []).map(parent => {
                if (parent.field_code !== parentDef.field_code) return parent;
                return {
                    ...parent,
                    child: value.trim()
                        ? [
                            ...(parent.child ?? []).filter(item => item.field_code !== def.field_code),
                            selection,
                        ]
                        : (parent.child ?? []).filter(item => item.field_code !== def.field_code),
                };
            });
            onChange({ conditionDataValues: next, value: "" });
        }
        clearRuleError(groupId, rule.id, "value");
    };

    useEffect(() => {
        conditionDataDefinitions.forEach(def => {
            const key = conditionOptionKey(conditionCode, def.field_code);
            const options = conditionCodeOptionsByKey?.[key] ?? [];
            const selected = rule.conditionDataValues?.find(item => item.field_code === def.field_code);

            if (def.children?.length && options.length === 1 && !selected?.value_str) {
                updateConditionDataValue(def, options[0]);
            }
        });
    }, [conditionDataDefinitions, conditionCode, conditionCodeOptionsByKey, rule.conditionDataValues]);

    const renderConditionDataSelect = (
        def: ConditionDataDefinition,
        parentDef?: ConditionDataDefinition,
        parentValue?: string
    ) => {
        const parentSelection = parentDef
            ? rule.conditionDataValues?.find(item => item.field_code === parentDef.field_code)
            : undefined;
        const options = getConditionDataOptions(
            conditionCodeOptionsByKey,
            def,
            conditionCode,
            parentDef,
            parentSelection?.value_str ?? parentValue
        );
        const selected = parentDef
            ? parentSelection?.child?.find(item => item.field_code === def.field_code)
            : rule.conditionDataValues?.find(item => item.field_code === def.field_code);
        const autoSelectedSingleRootValue = !parentDef && def.children?.length && options.length === 1
            ? String(options[0].value ?? options[0].option_id)
            : "";
        const selectedValue = selected?.value_str ?? autoSelectedSingleRootValue;
        const selectedParentValue = parentSelection?.value_str ?? parentValue ?? "";

        if (parentDef && !selectedParentValue) {
            return null;
        }

        if (
            parentDef &&
            isConditionDataOptionInput(def.input_type) &&
            !options.length &&
            !selectedValue
        ) {
            return null;
        }

        if (def.input_type === "NONE") {
            return null;
        }

        const renderChildren = selectedValue && def.children?.length
            ? def.children.map(childDef =>
                renderConditionDataSelect(childDef, def, selectedValue)
            )
            : null;

        if (def.input_type === "MULTISELECT") {
            const selectedIds = new Set(parseCsvIds(selectedValue));
            const selectedMultiOptions = options.filter(opt =>
                selectedIds.has(String(opt.value ?? opt.option_id))
            );
            const parentSelectedValue = parentSelection?.value_str ?? parentValue;

            return (
                <React.Fragment key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}>
                    <Autocomplete
                        multiple
                        options={options}
                        getOptionLabel={(opt) => opt.display_label}
                        isOptionEqualToValue={(opt, val) => String(opt.value ?? opt.option_id) === String(val.value ?? val.option_id)}
                        value={selectedMultiOptions}
                        onChange={(_, newValues) => updateConditionDataMultiValue(def, newValues, parentDef)}
                        onInputChange={(_, value, reason) => {
                            const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
                            if (reason === "input") {
                                activeSearchQueryRef.current[key] = value;
                                debouncedFetchConditionDataOptions(def, parentSelectedValue, value);
                            } else if (reason === "clear") {
                                activeSearchQueryRef.current[key] = "";
                                debouncedFetchConditionDataOptions(def, parentSelectedValue, "");
                            } else if (reason === "reset") {
                                // MULTISELECT: value is always "" on reset (after selection or blur)
                                // always re-fetch if we had a stale search so next open shows full list
                                if (activeSearchQueryRef.current[key]) {
                                    activeSearchQueryRef.current[key] = "";
                                    debouncedFetchConditionDataOptions(def, parentSelectedValue, "");
                                }
                            }
                        }}
                        filterOptions={(x) => x}
                        disabled={!conditionCode}
                        onOpen={() => {
                            const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
                            if (activeSearchQueryRef.current[key]) {
                                activeSearchQueryRef.current[key] = "";
                                fetchConditionDataOptions(def, parentSelectedValue, "", 1);
                            }
                        }}
                        ListboxProps={{
                            style: { maxHeight: 230 },
                            onScroll: (e) => handleConditionListboxScroll(e as any, def, parentSelectedValue),
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.display_label}
                                    {...getTagProps({ index })}
                                    size="small"
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                size="small"
                                error={errorState?.value}
                                helperText={errorState?.value ? "Required" : ""}
                                placeholder={def.display_name}
                            />
                        )}
                        sx={{
                            flex: 1,
                            minWidth: 220,
                            "& .MuiChip-label": {
                                margin: 0,
                                fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
                                fontWeight: 500,
                                fontSize: "1rem",
                                letterSpacing: "0.00938em",
                            },
                        }}
                    />
                    {renderChildren}
                </React.Fragment>
            );
        }

        if (isConditionDataFreeInput(def.input_type)) {
            return (
                <React.Fragment key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}>
                    <TextField
                        type={def.input_type === "INTEGER" ? "number" : "text"}
                        value={selectedValue}
                        onChange={event => updateConditionDataTextValue(def, event.target.value, parentDef)}
                        placeholder={def.display_name}
                        size="small"
                        variant="standard"
                        multiline={def.input_type === "MULTILINETEXT"}
                        rows={def.input_type === "MULTILINETEXT" ? 2 : undefined}
                        sx={{ flex: 1, minWidth: 220 }}
                        error={Boolean(errorState?.value && !selectedValue?.trim())}
                        helperText={errorState?.value && !selectedValue?.trim() ? "Required" : ""}
                    />
                    {renderChildren}
                </React.Fragment>
            );
        }

        const parentSelectedValue = parentSelection?.value_str ?? parentValue;
        const selectedOption = options.find(opt => String(opt.value ?? opt.option_id) === String(selectedValue)) ?? null;

        return (
            <React.Fragment key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}>
                <Autocomplete
                    options={options}
                    getOptionLabel={(opt) => opt.display_label}
                    isOptionEqualToValue={(opt, val) => String(opt.value ?? opt.option_id) === String(val.value ?? val.option_id)}
                    value={selectedOption}
                    onChange={(_, newValue) => {
                        if (newValue) {
                            updateConditionDataValue(def, newValue, parentDef);
                        } else {
                            // User cleared the selection — remove this field from state
                            const next = !parentDef
                                ? (rule.conditionDataValues ?? []).filter(item => item.field_code !== def.field_code)
                                : (rule.conditionDataValues ?? []).map(parent => {
                                    if (parent.field_code !== parentDef.field_code) return parent;
                                    return { ...parent, child: (parent.child ?? []).filter(item => item.field_code !== def.field_code) };
                                });
                            onChange({ conditionDataValues: next, value: "" });
                        }
                    }}
                    onInputChange={(_, value, reason) => {
                        const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
                        if (reason === "input") {
                            activeSearchQueryRef.current[key] = value;
                            debouncedFetchConditionDataOptions(def, parentSelectedValue, value);
                        } else if (reason === "clear") {
                            activeSearchQueryRef.current[key] = "";
                            debouncedFetchConditionDataOptions(def, parentSelectedValue, "");
                        } else if (reason === "reset") {
                            // SELECT: value="" means blur without selection, value=label means selection made.
                            // Only re-fetch when blur (value="") to avoid stale list on next open.
                            // On selection, just clear the flag (don't re-fetch so selected item stays visible).
                            if (activeSearchQueryRef.current[key]) {
                                activeSearchQueryRef.current[key] = "";
                                if (!value) {
                                    debouncedFetchConditionDataOptions(def, parentSelectedValue, "");
                                }
                            }
                        }
                    }}
                    filterOptions={(x) => x}
                    disabled={!conditionCode}
                    onOpen={() => {
                        const key = conditionOptionKey(conditionCode, def.field_code, parentSelectedValue);
                        if (activeSearchQueryRef.current[key]) {
                            activeSearchQueryRef.current[key] = "";
                            fetchConditionDataOptions(def, parentSelectedValue, "", 1);
                        }
                    }}
                    ListboxProps={{
                        style: { maxHeight: 230 },
                        onScroll: (e) => handleConditionListboxScroll(e as any, def, parentSelectedValue),
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            error={errorState?.value}
                            helperText={errorState?.value ? "Required" : ""}
                            placeholder={def.display_name}
                        />
                    )}
                    sx={{ flex: 1, minWidth: 220 }}
                />
                {renderChildren}
            </React.Fragment>
        );
    };

    return (
        <Paper sx={RULE_CHIP_ROW_SX}>
            {/* FIELD */}
            <Box sx={RULE_CHIP_PRIMARY_SX}>
                <Autocomplete
                    options={triggers}
                    getOptionLabel={(option) => option.label}
                    value={selectedTrigger ?? null}
                    onChange={(_, newValue) => {
                        if (!newValue) {
                            // Clear trigger — reset all condition data
                            onChange({ field: "", conditionEvaluator: "", value: "", conditionDataValues: [] });
                            return;
                        }
                        const defaultOperator =
                            OPERATOR_MAP[newValue.value_input_type ?? "TEXT"]?.[0] ?? "";
                        onChange({
                            field: newValue.label,
                            conditionEvaluator: defaultOperator,
                            value: newValue.value_input_type === "NONE" ? "" : "",
                            conditionDataValues: [],
                        });
                        clearRuleError(groupId, rule.id, "field");
                    }}
                    disabled={!triggers.length || !selectedEventId}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    ListboxProps={{ style: { maxHeight: 230 } }}
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            key={option.label}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <span>{option.label}</span>
                            {option.description && (
                                <Tooltip
                                    title={option.description}
                                    placement="right"
                                    arrow
                                    enterDelay={200}
                                    leaveDelay={100}
                                    PopperProps={{
                                        modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
                                    }}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: "#212529",
                                                color: "#fff",
                                                fontSize: "0.95rem",
                                                borderRadius: "4px",
                                                padding: "6px 8px",
                                                maxWidth: 260,
                                                maxHeight: 150,
                                                overflowY: "auto",  // tooltip scroll if content is tall
                                                whiteSpace: "normal",
                                            },
                                        },
                                        arrow: { sx: { color: "#212529" } },
                                    }}
                                >
                                    <span
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            color: "darkgray",
                                        }}
                                    >
                                        <InfoOutlinedIcon fontSize="small" />
                                    </span>
                                </Tooltip>
                            )}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            error={errorState?.field}
                            helperText={errorState?.field ? "Required" : ""}
                        />
                    )}
                />
            </Box>

            {/* OPERATOR */}
            {!conditionDataDefinitions.length && operatorList.length > 0 && (
                <Select
                    value={rule.conditionEvaluator ?? operatorList[0]}
                    onChange={e => {
                        onChange({ conditionEvaluator: String(e.target.value) });
                        clearRuleError(groupId, rule.id, "conditionEvaluator");
                    }}
                    size="small"
                    variant="standard"
                    sx={RULE_CHIP_OPERATOR_SX}
                    error={errorState?.operator}
                    disabled={selectedTrigger?.value_input_type === "NONE"}
                >
                    {operatorList.map(op => (
                        <MenuItem key={op} value={op}>
                            {op}
                        </MenuItem>
                    ))}
                </Select>
            )}

            {/* VALUE */}
            <Box sx={RULE_CHIP_VALUES_SX}>
            {conditionDataDefinitions.length ? (
                <>
                    {conditionDataDefinitions.map(def => renderConditionDataSelect(def))}
                </>
            ) : selectedTrigger?.value_input_type === "SELECT" ? (
                <Select
                    value={rule.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearRuleError(groupId, rule.id, "value");
                    }}
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                    error={errorState?.value}
                    disabled={!selectedTrigger.value_options?.length}
                >
                    {selectedTrigger.value_options?.map(opt => (
                        <MenuItem key={opt.option_id} value={opt.display_label}>
                            {opt.display_label}
                        </MenuItem>
                    ))}
                </Select>
            ) : selectedTrigger?.value_input_type === "MULTISELECT" ? (
                <Autocomplete
                    multiple
                    options={selectedTrigger.value_options ?? []}
                    getOptionLabel={(opt) => opt.display_label}
                    isOptionEqualToValue={(opt, val) => opt.option_id === val.option_id}
                    value={(selectedTrigger.value_options ?? []).filter(opt =>
                        parseCsvIds(rule.value).includes(String(opt.option_id))
                    )}
                    onChange={(_, newValues) => {
                        onChange({ value: toCsvIds(newValues.map(v => String(v.option_id))) });
                        clearRuleError(groupId, rule.id, "value");
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option.display_label}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            error={errorState?.value}
                            helperText={errorState?.value ? "Required" : ""}
                            placeholder="Select options"
                        />
                    )}
                    sx={{
                        flex: 1,
                        "& .MuiChip-label": {
                            margin: 0,
                            fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
                            fontWeight: 500,
                            fontSize: "1rem",
                            letterSpacing: "0.00938em",
                        },
                    }}
                />
            ) : selectedTrigger?.value_input_type === "BOOLEAN" ? (
                <Select
                    value={rule.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearRuleError(groupId, rule.id, "value");
                    }}
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                </Select>
            ) : selectedTrigger?.value_input_type === "INTEGER" ? (
                <TextField
                    type="number"
                    value={rule.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearRuleError(groupId, rule.id, "value");
                    }}
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                    error={errorState?.value}
                />
            ) : selectedTrigger?.value_input_type === "NONE" ? (
                <TextField
                    disabled
                    value=""
                    placeholder="No value required"
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                />
            ) : selectedTrigger?.value_input_type === "MULTILINETEXT" ? (
                <TextField
                    value={rule.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearRuleError(groupId, rule.id, "value");
                    }}
                    placeholder="Enter value"
                    size="small"
                    variant="standard"
                    multiline
                    rows={2}
                    sx={{ flex: 1 }}
                    helperText={errorState?.value ? "Required" : ""}
                />
            ) : (
                // TEXT (default — unchanged)
                <TextField
                    value={rule.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearRuleError(groupId, rule.id, "value");
                    }}
                    placeholder="Enter value"
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                    error={errorState?.value}
                    helperText={errorState?.value ? "Required" : ""}
                />
            )}
            </Box>
            {onDelete && (<IconButton onClick={onDelete} color="error" size="small" sx={{ flexShrink: 0 }}>
                <DeleteOutlineIcon />
            </IconButton>)}
        </Paper>
    );
}

// ---------- ActionChip ----------
function ActionChip({
    action,
    actionDefs,
    onChange,
    onDelete,
    errorState,
    clearActionError,
    selectedEventId,
    selectedPlatformId,
    allInventoryLocationList,
    actionCodeOptions
}: {
    action: Action;
    actionDefs: ActionDefinition[];
    onChange: (patch: Partial<Action>) => void;
    onDelete?: () => void;
    errorState?: { type?: boolean; value?: boolean };
    clearActionError: (actionId: string, field: keyof Action) => void;
    selectedEventId: number | "";
    selectedPlatformId: number | "";
    allInventoryLocationList: any[];
    actionCodeOptions: Record<string, ConditionCodeOption[]>;
}) {
    const dispatch = useAppDispatch();
    const { actionCodeHasMore } = useSelector((state: any) => state.ruleBuilderSlice);
    const selectedActionDef = React.useMemo(() => actionDefs.find(a => a.label === action.type),
        [actionDefs, action.type]);
    const actionDataDefinitions = selectedActionDef?.action_data ?? [];
    const searchTimeoutRef = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const pageRef = React.useRef<Record<string, number>>({});
    const isFetchingRef = React.useRef<Record<string, boolean>>({});
    const abortRef = React.useRef<Record<string, { abort: () => void } & Promise<any>>>({});
    // Tracks the last typed search per key so onOpen can restore full list if user blurred mid-search
    const activeSearchQueryRef = React.useRef<Record<string, string>>({}); 

    React.useEffect(() => () => {
        Object.values(searchTimeoutRef.current).forEach(t => clearTimeout(t));
    }, []);

    const fetchActionCodeOptions = (search?: string, page = 1) => {
        const actionCode = selectedActionDef?.action_code;
        if (!actionCode) return;

        const key = actionCode;
        pageRef.current[key] = page;

        abortRef.current[key]?.abort();
        const thunkResult = dispatch(getSelectMultiselectByActionCodeAsync({
            actionCode,
            limit: CONDITION_CODE_DATA_LIMIT,
            page,
            platformId: selectedPlatformId,
            ...(search?.trim() ? { search: search.trim() } : {}),
        })) as any;
        abortRef.current[key] = thunkResult;
        thunkResult.finally(() => {
            if (abortRef.current[key] === thunkResult) {
                isFetchingRef.current[key] = false;
            }
        });
    };

    const debouncedFetchActionCodeOptions = (search?: string) => {
        const actionCode = selectedActionDef?.action_code;
        if (!actionCode) return;
        if (searchTimeoutRef.current[actionCode]) clearTimeout(searchTimeoutRef.current[actionCode]);
        searchTimeoutRef.current[actionCode] = setTimeout(() => {
            pageRef.current[actionCode] = 1;
            fetchActionCodeOptions(search, 1);
        }, SEARCH_DEBOUNCE_MS);
    };

    const fetchActionDataOptions = (
        def: ConditionDataDefinition,
        parentSelectedValue?: string,
        search?: string,
        page = 1
    ) => {
        const actionCode = selectedActionDef?.action_code;
        if (
            !actionCode ||
            !def.field_code ||
            (def.input_type !== "SELECT" && def.input_type !== "MULTISELECT")
        ) return;

        const key = actionOptionKey(actionCode, def.field_code, parentSelectedValue);
        pageRef.current[key] = page;

        abortRef.current[key]?.abort();
        const thunkResult = dispatch(getSelectMultiselectByActionCodeAsync({
            actionCode,
            fieldCode: def.field_code,
            limit: CONDITION_CODE_DATA_LIMIT,
            page,
            platformId: selectedPlatformId,
            ...(parentSelectedValue ? { selectedValue: parentSelectedValue } : {}),
            ...(search?.trim() ? { search: search.trim() } : {}),
        })) as any;
        abortRef.current[key] = thunkResult;
        thunkResult.finally(() => {
            if (abortRef.current[key] === thunkResult) {
                isFetchingRef.current[key] = false;
            }
        });
    };

    const debouncedFetchActionDataOptions = (
        def: ConditionDataDefinition,
        parentSelectedValue?: string,
        search?: string
    ) => {
        const actionCode = selectedActionDef?.action_code;
        const key = actionOptionKey(actionCode, def.field_code, parentSelectedValue);
        if (searchTimeoutRef.current[key]) clearTimeout(searchTimeoutRef.current[key]);
        searchTimeoutRef.current[key] = setTimeout(() => {
            pageRef.current[key] = 1;
            fetchActionDataOptions(def, parentSelectedValue, search, 1);
        }, SEARCH_DEBOUNCE_MS);
    };

    const handleActionListboxScroll = (
        event: React.UIEvent<HTMLUListElement>,
        def: ConditionDataDefinition,
        parentSelectedValue?: string
    ) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const actionCode = selectedActionDef?.action_code;
        const key = actionOptionKey(actionCode, def.field_code, parentSelectedValue);
        const hasMore = Boolean(actionCodeHasMore?.[key]);

        if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !isFetchingRef.current[key]) {
            isFetchingRef.current[key] = true;
            const currentPage = pageRef.current[key] ?? 1;
            const nextPage = currentPage + 1;
            fetchActionDataOptions(def, parentSelectedValue, undefined, nextPage);
        }
    };

    const updateActionDataValue = (
        def: ConditionDataDefinition,
        value: string,
        option?: ConditionCodeOption,
        parentDef?: ConditionDataDefinition
    ) => {
        const existing = !parentDef
            ? (action.actionDataValues ?? []).find(item => item.field_code === def.field_code)
            : (action.actionDataValues ?? [])
                .find(item => item.field_code === parentDef.field_code)
                ?.child?.find(item => item.field_code === def.field_code);

        const selection: ActionDataSelection = {
            ...(existing?.id ? { id: existing.id } : {}),
            field_code: def.field_code,
            field_type: def.field_type ?? null,
            action_value: value,
            platform_field_id: def.field_type === "WORKFLOW" ? null : option?.platform_field_id ?? null,
            platform_field_data_id: def.field_type === "WORKFLOW"
                ? null
                : option?.platform_field_data_id ?? option?.option_id ?? null,
        };

        if (!parentDef) {
            const next = value
                ? [
                    ...(action.actionDataValues ?? []).filter(item => item.field_code !== def.field_code),
                    selection,
                ]
                : (action.actionDataValues ?? []).filter(item => item.field_code !== def.field_code);
            onChange({ actionDataValues: next, value: "" });
        } else {
            const next = (action.actionDataValues ?? []).map(parent => {
                if (parent.field_code !== parentDef.field_code) return parent;
                return {
                    ...parent,
                    child: value
                        ? [
                            ...(parent.child ?? []).filter(item => item.field_code !== def.field_code),
                            selection,
                        ]
                        : (parent.child ?? []).filter(item => item.field_code !== def.field_code),
                };
            });
            onChange({ actionDataValues: next, value: "" });
        }
        clearActionError(action.id, "value");
    };

    const updateActionDataMultiValue = (
        def: ConditionDataDefinition,
        selectedOptions: ConditionCodeOption[],
        parentDef?: ConditionDataDefinition
    ) => {
        const ids = selectedOptions.map(opt => String(opt.value ?? opt.option_id));
        const dataIds = selectedOptions
            .map(opt => opt.platform_field_data_id ?? opt.option_id)
            .filter(value => value !== null && value !== undefined)
            .map(String);
        const option = selectedOptions[0];
        const existing = !parentDef
            ? (action.actionDataValues ?? []).find(item => item.field_code === def.field_code)
            : (action.actionDataValues ?? [])
                .find(item => item.field_code === parentDef.field_code)
                ?.child?.find(item => item.field_code === def.field_code);

        const selection: ActionDataSelection = {
            ...(existing?.id ? { id: existing.id } : {}),
            field_code: def.field_code,
            field_type: def.field_type ?? null,
            action_value: toCsvIds(ids),
            platform_field_id: def.field_type === "WORKFLOW" ? null : option?.platform_field_id ?? null,
            platform_field_data_id: def.field_type === "WORKFLOW"
                ? null
                : dataIds.length ? toCsvIds(dataIds) : null,
        };

        if (!parentDef) {
            const next = selectedOptions.length
                ? [
                    ...(action.actionDataValues ?? []).filter(item => item.field_code !== def.field_code),
                    selection,
                ]
                : (action.actionDataValues ?? []).filter(item => item.field_code !== def.field_code);
            onChange({ actionDataValues: next, value: "" });
        } else {
            const next = (action.actionDataValues ?? []).map(parent => {
                if (parent.field_code !== parentDef.field_code) return parent;
                return {
                    ...parent,
                    child: selectedOptions.length
                        ? [
                            ...(parent.child ?? []).filter(item => item.field_code !== def.field_code),
                            selection,
                        ]
                        : (parent.child ?? []).filter(item => item.field_code !== def.field_code),
                };
            });
            onChange({ actionDataValues: next, value: "" });
        }
        clearActionError(action.id, "value");
    };

    useEffect(() => {
        const actionCode = selectedActionDef?.action_code;
        if (!actionCode) return;

        actionDataDefinitions.forEach(def => {
            const key = actionOptionKey(actionCode, def.field_code);
            if (!actionCodeOptions?.[key]?.length) fetchActionDataOptions(def);

            const selected = action.actionDataValues?.find(item => item.field_code === def.field_code);
            if (selected?.action_value && def.children?.length) {
                def.children.forEach(childDef => {
                    const childKey = actionOptionKey(actionCode, childDef.field_code, selected.action_value);
                    if (!actionCodeOptions?.[childKey]?.length) {
                        fetchActionDataOptions(childDef, selected.action_value);
                    }
                });
            }
        });
    }, [actionDataDefinitions, selectedActionDef?.action_code, action.actionDataValues, actionCodeOptions]);

    // useEffect(() => {
    //     if (!selectedEventId) return;

    //     const actionCode = selectedActionDef?.action_code;
    //     if (!actionCode) return;

    //     const apiType = ACTION_PLATFORM_API_MAP[actionCode];

    //     if (apiType === "SECONDARY" && !selectedActionDef?.value_options?.length) {
    //         dispatch(getAllSecondaryPlatformListAsync());
    //     }

    //     if (apiType === "NON_SECONDARY" && !selectedActionDef?.value_options?.length) {
    //         dispatch(getAllNonSecondaryPlatformListAsync());
    //     }

    //     if (apiType === "INVENTORY_LOCATION" && !selectedActionDef?.value_options?.length) {
    //         dispatch(getAllInventoryLocationPlatformListAsync());
    //     }
    // }, [
    //     selectedEventId,
    //     selectedActionDef?.action_code,
    //     selectedActionDef?.value_options?.length,
    //     allInventoryLocationList?.length,
    //     dispatch,
    // ]);

    const resolvedValueOptions = React.useMemo<ConditionCodeOption[]>(() => {
        const actionCode = selectedActionDef?.action_code;
        const inputType = selectedActionDef?.value_input_type;

        if (
            actionCode &&
            (inputType === "SELECT" || inputType === "MULTISELECT") &&
            actionCodeOptions?.[actionCode]?.length
        ) {
            return actionCodeOptions[actionCode];
        }

        return (selectedActionDef?.value_options ?? []) as ConditionCodeOption[];
    }, [
        selectedActionDef?.action_code,
        selectedActionDef?.value_input_type,
        selectedActionDef?.value_options,
        actionCodeOptions,
    ]);

    useEffect(() => {
        if (!selectedEventId) return;

        const actionCode = selectedActionDef?.action_code;
        const inputType = selectedActionDef?.value_input_type;

        if (!actionCode) return;
        if (inputType !== "SELECT" && inputType !== "MULTISELECT") return;

        const alreadyLoaded = actionCodeOptions?.[actionCode]?.length > 0;
        if (alreadyLoaded) return;

        fetchActionCodeOptions();
    }, [
        selectedEventId,
        selectedActionDef?.action_code,
        selectedActionDef?.value_input_type,
        actionCodeOptions,
        dispatch,
    ]);
    const selectedIds = React.useMemo(() => parseCsvIds(action.value), [action.value]);

    const selectedMultiOptions = React.useMemo(() => {
        const set = new Set(selectedIds);
        return resolvedValueOptions.filter(o => set.has(String(o.option_id)));
    }, [resolvedValueOptions, selectedIds]);

    const renderActionDataInput = (
        def: ConditionDataDefinition,
        parentDef?: ConditionDataDefinition,
        parentValue?: string
    ): React.ReactNode => {
        const actionCode = selectedActionDef?.action_code;
        const parentSelection = parentDef
            ? action.actionDataValues?.find(item => item.field_code === parentDef.field_code)
            : undefined;
        const selected = parentDef
            ? parentSelection?.child?.find(item => item.field_code === def.field_code)
            : action.actionDataValues?.find(item => item.field_code === def.field_code);
        const selectedValue = selected?.action_value ?? "";
        const selectedParentValue = parentSelection?.action_value ?? parentValue;
        const options = getActionDataOptions(
            actionCodeOptions,
            def,
            actionCode,
            parentDef,
            selectedParentValue
        );
        const renderChildren = selectedValue && def.children?.map(childDef =>
            renderActionDataInput(childDef, def, selectedValue)
        );

        if (parentDef && !selectedParentValue) return null;

        if (def.input_type === "SELECT") {
            const selectedOption = options.find(opt =>
                String(opt.value ?? opt.option_id) === String(selectedValue)
            ) ?? null;
            return (
                <React.Fragment key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}>
                    <Autocomplete
                        options={options}
                        getOptionLabel={option => option.display_label}
                        isOptionEqualToValue={(option, value) =>
                            String(option.value ?? option.option_id) === String(value.value ?? value.option_id)
                        }
                        value={selectedOption}
                        onChange={(_, newValue) => updateActionDataValue(
                            def,
                            newValue ? String(newValue.value ?? newValue.option_id) : "",
                            newValue ?? undefined,
                            parentDef
                        )}
                        onInputChange={(_, value, reason) => {
                            const actionCode = selectedActionDef?.action_code;
                            const key = actionOptionKey(actionCode, def.field_code, selectedParentValue);
                            if (reason === "input") {
                                activeSearchQueryRef.current[key] = value;
                                debouncedFetchActionDataOptions(def, selectedParentValue, value);
                            } else if (reason === "clear") {
                                activeSearchQueryRef.current[key] = "";
                                debouncedFetchActionDataOptions(def, selectedParentValue, "");
                            } else if (reason === "reset") {
                                if (activeSearchQueryRef.current[key]) {
                                    activeSearchQueryRef.current[key] = "";
                                    if (!value) {
                                        debouncedFetchActionDataOptions(def, selectedParentValue, "");
                                    }
                                }
                            }
                        }}
                        filterOptions={items => items}
                        onOpen={() => {
                            const actionCode = selectedActionDef?.action_code;
                            const key = actionOptionKey(actionCode, def.field_code, selectedParentValue);
                            if (activeSearchQueryRef.current[key]) {
                                activeSearchQueryRef.current[key] = "";
                                fetchActionDataOptions(def, selectedParentValue, "", 1);
                            }
                        }}
                        ListboxProps={{
                            style: { maxHeight: 230 },
                            onScroll: (e) => handleActionListboxScroll(e as any, def, selectedParentValue),
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="standard"
                                size="small"
                                placeholder={def.display_name}
                                error={errorState?.value && !selectedValue}
                            />
                        )}
                        sx={RULE_CHIP_ACTION_FIELD_SX}
                    />
                    {renderChildren}
                </React.Fragment>
            );
        }

        if (def.input_type === "MULTISELECT") {
            const selectedIdSet = new Set(parseCsvIds(selectedValue));
            const selectedOptions = options.filter(option =>
                selectedIdSet.has(String(option.value ?? option.option_id))
            );
            return (
                <React.Fragment key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}>
                    <Autocomplete
                        multiple
                        options={options}
                        getOptionLabel={option => option.display_label}
                        isOptionEqualToValue={(option, value) =>
                            String(option.value ?? option.option_id) === String(value.value ?? value.option_id)
                        }
                        value={selectedOptions}
                        onChange={(_, newValues) => updateActionDataMultiValue(def, newValues, parentDef)}
                        onInputChange={(_, value, reason) => {
                            const actionCode = selectedActionDef?.action_code;
                            const key = actionOptionKey(actionCode, def.field_code, selectedParentValue);
                            if (reason === "input") {
                                activeSearchQueryRef.current[key] = value;
                                debouncedFetchActionDataOptions(def, selectedParentValue, value);
                            } else if (reason === "clear") {
                                activeSearchQueryRef.current[key] = "";
                                debouncedFetchActionDataOptions(def, selectedParentValue, "");
                            } else if (reason === "reset") {
                                if (activeSearchQueryRef.current[key]) {
                                    activeSearchQueryRef.current[key] = "";
                                    debouncedFetchActionDataOptions(def, selectedParentValue, "");
                                }
                            }
                        }}
                        filterOptions={items => items}
                        onOpen={() => {
                            const actionCode = selectedActionDef?.action_code;
                            const key = actionOptionKey(actionCode, def.field_code, selectedParentValue);
                            if (activeSearchQueryRef.current[key]) {
                                activeSearchQueryRef.current[key] = "";
                                fetchActionDataOptions(def, selectedParentValue, "", 1);
                            }
                        }}
                        ListboxProps={{
                            style: { maxHeight: 230 },
                            onScroll: (e) => handleActionListboxScroll(e as any, def, selectedParentValue),
                        }}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                            <Chip
                                label={option.display_label}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="standard"
                                size="small"
                                placeholder={def.display_name}
                                error={errorState?.value && !selectedValue}
                            />
                        )}
                        sx={RULE_CHIP_ACTION_FIELD_SX}
                    />
                    {renderChildren}
                </React.Fragment>
            );
        }

        if (def.input_type === "BOOLEAN") {
            return (
                <FormControl
                    key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}
                    variant="standard"
                    sx={{ flex: 1, minWidth: 180 }}
                    error={errorState?.value && !selectedValue}
                >
                    <Typography variant="caption" color="text.secondary">{def.display_name}</Typography>
                    <Select
                        value={selectedValue}
                        onChange={event => updateActionDataValue(def, String(event.target.value), undefined, parentDef)}
                    >
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                    </Select>
                    {renderChildren}
                </FormControl>
            );
        }

        if (def.input_type === "NONE") {
            return null;
        }

        return (
            <React.Fragment key={`${parentDef?.field_code ?? "root"}-${def.field_code}`}>
                <TextField
                    type={def.input_type === "INTEGER" ? "number" : "text"}
                    value={selectedValue}
                    onChange={event => updateActionDataValue(def, event.target.value, undefined, parentDef)}
                    placeholder={def.display_name}
                    size="small"
                    variant="standard"
                    multiline={def.input_type === "MULTILINETEXT"}
                    rows={def.input_type === "MULTILINETEXT" ? 2 : undefined}
                    sx={RULE_CHIP_ACTION_FIELD_SX}
                    error={errorState?.value && !selectedValue}
                    helperText={errorState?.value && !selectedValue ? "Required" : ""}
                />
                {renderChildren}
            </React.Fragment>
        );
    };

    return (
        <Paper sx={RULE_CHIP_ROW_SX}>
            { }
            <Box sx={RULE_CHIP_PRIMARY_SX}>
                <Autocomplete
                    options={actionDefs}
                    getOptionLabel={(option) => option.label}
                    value={selectedActionDef ?? null}
                    onChange={(_, newValue) => {
                        if (!newValue) {
                            // Clear action type — reset action value and data
                            onChange({ type: "", value: "", actionDataValues: [] });
                            return;
                        }
                        onChange({ type: newValue.label, value: "", actionDataValues: [] });
                        clearActionError(action.id, "type");
                    }}
                    disabled={!actionDefs.length || !selectedEventId}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    ListboxProps={{ style: { maxHeight: 230 } }}
                    renderOption={(props, option) => (
                        <li
                            {...props}
                            key={option.label}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 4,
                            }}
                        >
                            <span>{option.label}</span>
                            {option.description && (
                                <Tooltip
                                    title={option.description}
                                    placement="right"
                                    arrow
                                    enterDelay={200}
                                    leaveDelay={100}
                                    PopperProps={{
                                        modifiers: [{ name: "offset", options: { offset: [0, 6] } }],
                                    }}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: "#212529", color: "#fff", fontSize: "0.95rem", borderRadius: "4px", padding: "6px 8px", maxWidth: 260, maxHeight: 150,
                                                overflowY: "auto",
                                                whiteSpace: "normal",
                                            },
                                        },
                                        arrow: { sx: { color: "#212529" } },
                                    }}
                                >
                                    <span
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            display: "flex", alignItems: "center", cursor: "pointer", color: "darkgray",
                                        }}
                                    >
                                        <InfoOutlinedIcon fontSize="small" />
                                    </span>
                                </Tooltip>
                            )}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            error={errorState?.type}
                            helperText={errorState?.type ? "Required" : ""}
                        />
                    )}
                />
            </Box>

            {/* ---------- VALUE FIELD (ONLY LOGIC ADDED) ---------- */}
            <Box sx={RULE_CHIP_VALUES_SX}>
            {actionDataDefinitions.length ? (
                <Stack direction="row" spacing={2} sx={{ flex: 1, width: "100%", flexWrap: "wrap", rowGap: 2, alignItems: "flex-end" }}>
                    {actionDataDefinitions.map(def => renderActionDataInput(def))}
                </Stack>
            ) : selectedActionDef?.value_input_type === "MULTISELECT" ? (
                <Autocomplete
                    multiple
                    options={resolvedValueOptions}
                    getOptionLabel={(opt) => opt.display_label}
                    isOptionEqualToValue={(opt, val) => opt.option_id === val.option_id}
                    value={selectedMultiOptions}
                    onChange={(_, newValues) => {
                        const ids = newValues.map(v => String(v.option_id));
                        onChange({ value: toCsvIds(ids) });
                        clearActionError(action.id, "value");
                    }}
                    onInputChange={(_, value, reason) => {
                        const actionCode = selectedActionDef?.action_code ?? "";
                        if (reason === "input") {
                            activeSearchQueryRef.current[actionCode] = value;
                            debouncedFetchActionCodeOptions(value);
                        } else if (reason === "clear") {
                            activeSearchQueryRef.current[actionCode] = "";
                            debouncedFetchActionCodeOptions("");
                        } else if (reason === "reset") {
                            if (activeSearchQueryRef.current[actionCode]) {
                                activeSearchQueryRef.current[actionCode] = "";
                                debouncedFetchActionCodeOptions("");
                            }
                        }
                    }}
                    filterOptions={(x) => x}
                    onOpen={() => {
                        const actionCode = selectedActionDef?.action_code ?? "";
                        if (activeSearchQueryRef.current[actionCode]) {
                            activeSearchQueryRef.current[actionCode] = "";
                            fetchActionCodeOptions("", 1);
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option.display_label}
                                {...getTagProps({ index })}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            error={errorState?.value}
                            helperText={errorState?.value ? "Required" : ""}
                            placeholder="Search and select options"
                        />
                    )}
                    sx={{
                        flex: 1,
                        "& .MuiChip-label": {
                            margin: 0,
                            fontFamily: `"Roboto","Helvetica","Arial",sans-serif`,
                            fontWeight: 500,
                            fontSize: "1rem",
                            letterSpacing: "0.00938em",
                        },
                        "& .MuiAutocomplete-tag": {
                            borderRadius: "7px",
                        },
                    }}
                    disabled={!selectedActionDef?.action_code && !resolvedValueOptions.length}
                />
            ) : selectedActionDef?.value_input_type === "SELECT" ? (
                <Autocomplete
                    options={resolvedValueOptions}
                    getOptionLabel={(opt) => opt.display_label}
                    isOptionEqualToValue={(opt, val) => opt.option_id === val.option_id}
                    value={resolvedValueOptions.find(opt => String(opt.option_id) === String(action.value)) ?? null}
                    onChange={(_, newValue) => {
                        onChange({ value: newValue ? String(newValue.option_id) : "" });
                        clearActionError(action.id, "value");
                    }}
                    onInputChange={(_, value, reason) => {
                        const actionCode = selectedActionDef?.action_code ?? "";
                        if (reason === "input") {
                            activeSearchQueryRef.current[actionCode] = value;
                            debouncedFetchActionCodeOptions(value);
                        } else if (reason === "clear") {
                            activeSearchQueryRef.current[actionCode] = "";
                            debouncedFetchActionCodeOptions("");
                        } else if (reason === "reset") {
                            if (activeSearchQueryRef.current[actionCode]) {
                                activeSearchQueryRef.current[actionCode] = "";
                                if (!value) {
                                    debouncedFetchActionCodeOptions("");
                                }
                            }
                        }
                    }}
                    filterOptions={(x) => x}
                    onOpen={() => {
                        const actionCode = selectedActionDef?.action_code ?? "";
                        if (activeSearchQueryRef.current[actionCode]) {
                            activeSearchQueryRef.current[actionCode] = "";
                            fetchActionCodeOptions("", 1);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            error={errorState?.value}
                            helperText={errorState?.value ? "Required" : ""}
                            placeholder="Search and select"
                        />
                    )}
                    sx={{ flex: 1 }}
                    disabled={!selectedActionDef?.action_code && !resolvedValueOptions.length}
                />
            ) : selectedActionDef?.value_input_type === "BOOLEAN" ? (
                <Select
                    value={action.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearActionError(action.id, "value");
                    }}
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                    error={errorState?.value}
                >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                </Select>
            ) : selectedActionDef?.value_input_type === "INTEGER" ? (
                <TextField
                    type="number"
                    value={action.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearActionError(action.id, "value");
                    }}
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                    error={errorState?.value}
                    helperText={errorState?.value ? "Required" : ""}
                />
            ) : selectedActionDef?.value_input_type === "NONE" ? (
                <TextField
                    disabled
                    value=""
                    placeholder="No value required"
                    size="small"
                    variant="standard"
                    sx={{ flex: 1 }}
                />
            ) : selectedActionDef?.value_input_type === "MULTILINETEXT" ? (
                <TextField
                    value={action.value ?? ""}
                    onChange={e => {
                        onChange({ value: e.target.value });
                        clearActionError(action.id, "value");
                    }}
                    placeholder="Enter value"
                    size="small"
                    variant="standard"
                    multiline
                    rows={2}
                    sx={{ flex: 1 }}
                    error={errorState?.value}
                    helperText={errorState?.value ? "Required" : ""}
                />
            )
                : (
                    // TEXT (default – SAME AS CURRENT) 
                    <TextField
                        value={action.value ?? ""}
                        onChange={e => {
                            onChange({ value: e.target.value });
                            clearActionError(action.id, "value");
                        }}
                        placeholder="Enter value"
                        size="small"
                        variant="standard"
                        sx={{ flex: 1 }}
                        error={errorState?.value}
                        helperText={errorState?.value ? "Required" : ""}
                    />
                )
            }
            </Box>
            {onDelete && (
                <IconButton onClick={onDelete} color="error" size="small" sx={{ flexShrink: 0 }}>
                    <DeleteOutlineIcon />
                </IconButton>
            )}
        </Paper>
    );
}
